import { useState, useEffect } from "react";
import { useNavigate } from "react-router-dom";
import { ShoppingBag, Check, RefreshCw } from "lucide-react";
import api, { apiErr } from "@/lib/api";
import { toast } from "sonner";

const WEEKLY_ROTATION_ITEM_COUNT = 10;

const btn = (variant) => {
  if (variant === "primary") return "px-4 py-2 bg-amber-900 border border-amber-700 text-amber-100 rounded hover:bg-amber-800 transition-colors disabled:opacity-50";
  return "px-4 py-2 bg-zinc-800 border border-zinc-700 text-zinc-300 rounded hover:bg-zinc-700 transition-colors disabled:opacity-50";
};

export default function AdminMerchantManager() {
  const navigate = useNavigate();
  const [loading, setLoading] = useState(true);
  const [availableItems, setAvailableItems] = useState({ beast_boost_items: [], non_beast_items: [] });
  const [selectedItems, setSelectedItems] = useState([]);
  const [currentInventory, setCurrentInventory] = useState(null);
  const [alwaysAvailableConfig, setAlwaysAvailableConfig] = useState({ title: "", story: "", items: [] });
  const [busy, setBusy] = useState(false);
  const [priceEdits, setPriceEdits] = useState({});
  const [alwaysAvailableEdits, setAlwaysAvailableEdits] = useState({});

  useEffect(() => {
    loadData();
  }, []);

  const loadData = async () => {
    setLoading(true);
    try {
      // Load available items
      const { data: items } = await api.get("/merchant/admin/available-items");
      setAvailableItems(items);
      const allLoadedItems = [...(items.beast_boost_items || []), ...(items.non_beast_items || [])];

      const { data: alwaysAvailable } = await api.get("/merchant/admin/always-available-config");
      setAlwaysAvailableConfig(alwaysAvailable);
      const alwaysAvailableMap = Object.fromEntries(
        (alwaysAvailable.items || []).map((item) => [
          item.item_id,
          {
            enabled: true,
            purchase_limit: Number(item.purchase_limit || 1),
          },
        ])
      );

      setPriceEdits(
        Object.fromEntries(
          allLoadedItems.map((item) => [
            item.item_id,
            {
              price_gold: Number(item.price_gold || 0),
              price_follis: Number(item.price_follis || 0),
            },
          ])
        )
      );
      setAlwaysAvailableEdits(
        Object.fromEntries(
          allLoadedItems.map((item) => [
            item.item_id,
            alwaysAvailableMap[item.item_id] || {
              enabled: false,
              purchase_limit: 1,
            },
          ])
        )
      );

      // Load current week's actual inventory (the 10 rotating items customers see)
      const { data: inventory } = await api.get("/merchant/current-week-inventory");
      setCurrentInventory(inventory);
    } catch (err) {
      const errMsg = apiErr(err);
      if (err.response?.status === 403) {
        toast.error("Admin access required");
        navigate("/");
      } else {
        toast.error(errMsg);
      }
    } finally {
      setLoading(false);
    }
  };

  const toggleItem = (itemId) => {
    if (selectedItems.includes(itemId)) {
      setSelectedItems(selectedItems.filter(id => id !== itemId));
    } else {
      if (selectedItems.length >= WEEKLY_ROTATION_ITEM_COUNT) {
        toast.error(`Maximum ${WEEKLY_ROTATION_ITEM_COUNT} items allowed`);
        return;
      }
      setSelectedItems([...selectedItems, itemId]);
    }
  };

  const updatePrice = (itemId, field, value) => {
    const parsed = Number.parseInt(value, 10);
    const safeValue = Number.isNaN(parsed) || parsed < 0 ? 0 : parsed;
    setPriceEdits((prev) => ({
      ...prev,
      [itemId]: {
        ...(prev[itemId] || { price_gold: 0, price_follis: 0 }),
        [field]: safeValue,
      },
    }));
  };

  const updateAlwaysAvailable = (itemId, field, value) => {
    setAlwaysAvailableEdits((prev) => {
      const current = prev[itemId] || { enabled: false, purchase_limit: 1 };
      if (field === "enabled") {
        return {
          ...prev,
          [itemId]: {
            ...current,
            enabled: !current.enabled,
          },
        };
      }

      const parsed = Number.parseInt(value, 10);
      const safeValue = Number.isNaN(parsed) || parsed < 1 ? 1 : parsed;
      return {
        ...prev,
        [itemId]: {
          ...current,
          purchase_limit: safeValue,
        },
      };
    });
  };

  const savePrices = async () => {
    const allItems = [...availableItems.beast_boost_items, ...availableItems.non_beast_items];
    const invalidItem = allItems.find((item) => {
      const prices = priceEdits[item.item_id] || { price_gold: 0, price_follis: 0 };
      return Number(prices.price_gold || 0) === 0 && Number(prices.price_follis || 0) === 0;
    });

    if (invalidItem) {
      toast.error(`${invalidItem.name} must cost gold, follis, or both.`);
      return;
    }

    setBusy(true);
    try {
      await api.post("/merchant/admin/set-prices", {
        items: allItems.map((item) => ({
          item_id: item.item_id,
          price_gold: Number(priceEdits[item.item_id]?.price_gold || 0),
          price_follis: Number(priceEdits[item.item_id]?.price_follis || 0),
        })),
      });
      toast.success("Merchant prices updated!", { icon: "💰" });
      await loadData();
    } catch (err) {
      toast.error(apiErr(err));
    } finally {
      setBusy(false);
    }
  };

  const saveAlwaysAvailable = async () => {
    const allItems = [...availableItems.beast_boost_items, ...availableItems.non_beast_items];
    const enabledItems = allItems
      .filter((item) => alwaysAvailableEdits[item.item_id]?.enabled)
      .map((item) => ({
        item_id: item.item_id,
        purchase_limit: Number(alwaysAvailableEdits[item.item_id]?.purchase_limit || 1),
      }));

    setBusy(true);
    try {
      await api.post("/merchant/admin/always-available-config", { items: enabledItems });
      toast.success("Forum stewards shelf updated!", { icon: "🏺" });
      await loadData();
    } catch (err) {
      toast.error(apiErr(err));
    } finally {
      setBusy(false);
    }
  };

  const setMerchantInventory = async () => {
    if (selectedItems.length !== WEEKLY_ROTATION_ITEM_COUNT) {
      toast.error(`Must select exactly ${WEEKLY_ROTATION_ITEM_COUNT} items`);
      return;
    }

    setBusy(true);
    try {
      const response = await api.post("/merchant/admin/set-inventory", { item_ids: selectedItems });
      
      toast.success("Merchant inventory updated!", { icon: "✅" });
      
      // Clear selection immediately
      setSelectedItems([]);
      
      // Small delay before reloading to ensure DB update propagated
      await new Promise(resolve => setTimeout(resolve, 500));
      await loadData();
    } catch (err) {
      toast.error(apiErr(err));
    } finally {
      setBusy(false);
    }
  };

  const clearCustomInventory = async () => {
    if (!window.confirm("Clear custom inventory and revert to random generation?")) return;

    setBusy(true);
    try {
      await api.delete("/merchant/admin/clear-custom-inventory");
      toast.success("Reverted to random generation", { icon: "🔄" });
      setSelectedItems([]);
      await loadData();
    } catch (err) {
      toast.error(apiErr(err));
    } finally {
      setBusy(false);
    }
  };

  if (loading) {
    return (
      <div className="min-h-screen bg-zinc-950 flex items-center justify-center">
        <div className="text-zinc-400">Loading...</div>
      </div>
    );
  }

  const allItems = [...availableItems.beast_boost_items, ...availableItems.non_beast_items];

  const getEditedPrices = (item) => ({
    price_gold: Number(priceEdits[item.item_id]?.price_gold ?? item.price_gold ?? 0),
    price_follis: Number(priceEdits[item.item_id]?.price_follis ?? item.price_follis ?? 0),
  });

  const formatPriceSummary = (prices) => {
    const parts = [];
    if (Number(prices.price_gold || 0) > 0) parts.push(`${Number(prices.price_gold).toLocaleString()} Gold`);
    if (Number(prices.price_follis || 0) > 0) parts.push(`${Number(prices.price_follis).toLocaleString()} Follis`);
    return parts.join(" • ") || "No active price";
  };

  const formatDraftPriceSummary = (item) => formatPriceSummary(getEditedPrices(item));

  const getAlwaysAvailableSettings = (itemId) => alwaysAvailableEdits[itemId] || { enabled: false, purchase_limit: 1 };

  const selectedAlwaysAvailableItems = allItems.filter((item) => getAlwaysAvailableSettings(item.item_id).enabled);

  const renderItemCard = (item, tone = "amber") => {
    const isSelected = selectedItems.includes(item.item_id);
    const reserveSettings = getAlwaysAvailableSettings(item.item_id);
    const colors = tone === "purple"
      ? {
          selected: "bg-purple-950/30 border-purple-600",
          idle: "bg-zinc-950 border-zinc-800 hover:border-purple-700",
          button: isSelected ? "border-purple-500 bg-purple-900/50 text-purple-200" : "border-zinc-700 text-zinc-400 hover:border-purple-700 hover:text-purple-300",
          check: "text-purple-400",
          reserveButton: reserveSettings.enabled ? "border-emerald-500 bg-emerald-950/30 text-emerald-200" : "border-zinc-700 text-zinc-400 hover:border-emerald-700 hover:text-emerald-300",
        }
      : {
          selected: "bg-blue-950/30 border-blue-600",
          idle: "bg-zinc-950 border-zinc-800 hover:border-blue-700",
          button: isSelected ? "border-blue-500 bg-blue-900/50 text-blue-200" : "border-zinc-700 text-zinc-400 hover:border-blue-700 hover:text-blue-300",
          check: "text-blue-400",
          reserveButton: reserveSettings.enabled ? "border-emerald-500 bg-emerald-950/30 text-emerald-200" : "border-zinc-700 text-zinc-400 hover:border-emerald-700 hover:text-emerald-300",
        };

    return (
      <div
        key={item.item_id}
        data-testid={`merchant-item-card-${item.item_id}`}
        className={`rounded border p-4 transition-all ${isSelected ? colors.selected : colors.idle}`}
      >
        <div className="flex items-start justify-between gap-3 mb-2">
          <div>
            <div className="text-sm font-semibold text-zinc-200">{item.name}</div>
            <div data-testid={`merchant-item-price-summary-${item.item_id}`} className="text-xs text-amber-400 mt-1">
              {formatDraftPriceSummary(item)}
            </div>
          </div>
          <button
            type="button"
            data-testid={`merchant-item-select-${item.item_id}`}
            onClick={() => toggleItem(item.item_id)}
            className={`shrink-0 px-3 py-1 text-xs border rounded-sm transition-colors ${colors.button}`}
          >
            {isSelected ? "Selected" : "Select"}
          </button>
        </div>

        <div className="text-xs text-zinc-500 mb-3">{item.description}</div>

        <div className="grid grid-cols-2 gap-2">
          <label className="text-[11px] uppercase tracking-widest text-zinc-500">
            Gold
            <input
              data-testid={`merchant-item-price-gold-${item.item_id}`}
              type="number"
              min={0}
              value={priceEdits[item.item_id]?.price_gold ?? 0}
              onChange={(e) => updatePrice(item.item_id, "price_gold", e.target.value)}
              className="mt-1 w-full rounded border border-zinc-700 bg-zinc-900 px-3 py-2 text-sm text-zinc-100 outline-none focus:border-amber-500"
            />
          </label>
          <label className="text-[11px] uppercase tracking-widest text-zinc-500">
            Follis
            <input
              data-testid={`merchant-item-price-follis-${item.item_id}`}
              type="number"
              min={0}
              value={priceEdits[item.item_id]?.price_follis ?? 0}
              onChange={(e) => updatePrice(item.item_id, "price_follis", e.target.value)}
              className="mt-1 w-full rounded border border-zinc-700 bg-zinc-900 px-3 py-2 text-sm text-zinc-100 outline-none focus:border-amber-500"
            />
          </label>
        </div>

        <div className="mt-3 grid grid-cols-1 sm:grid-cols-[1fr_auto] gap-2 items-end">
          <label className="text-[11px] uppercase tracking-widest text-zinc-500">
            Forum Shelf Weekly Limit
            <input
              data-testid={`merchant-item-always-available-limit-${item.item_id}`}
              type="number"
              min={1}
              value={reserveSettings.purchase_limit}
              onChange={(e) => updateAlwaysAvailable(item.item_id, "purchase_limit", e.target.value)}
              className="mt-1 w-full rounded border border-zinc-700 bg-zinc-900 px-3 py-2 text-sm text-zinc-100 outline-none focus:border-emerald-500"
            />
          </label>
          <button
            type="button"
            data-testid={`merchant-item-toggle-always-available-${item.item_id}`}
            onClick={() => updateAlwaysAvailable(item.item_id, "enabled")}
            className={`px-3 py-2 text-xs border rounded-sm transition-colors ${colors.reserveButton}`}
          >
            {reserveSettings.enabled ? "On Forum Shelf" : "Add to Forum Shelf"}
          </button>
        </div>

        <div className="mt-2 flex items-center justify-between gap-2 text-[11px] text-zinc-500">
          <span>0 disables that currency. Forum shelf limits reset weekly.</span>
          <div className="flex items-center gap-2">
            {reserveSettings.enabled && <span className="text-emerald-400">Shelf ready</span>}
            {isSelected && <Check size={16} className={colors.check} />}
          </div>
        </div>
      </div>
    );
  };

  return (
    <div className="min-h-screen bg-zinc-950 text-zinc-100 p-6">
      <div className="max-w-7xl mx-auto">
        {/* Header */}
        <div className="mb-8">
          <h1 className="font-cinzel text-4xl text-amber-300 uppercase tracking-wide mb-2 flex items-center gap-3">
            <ShoppingBag size={36} />
            Manage Traveling Merchant
          </h1>
          <p className="text-zinc-400">Set custom weekly inventory or use random generation</p>
        </div>

        {/* Current Inventory Status */}
        <div className="bg-zinc-900 border border-amber-900/30 rounded-lg p-6 mb-6">
          <h2 className="font-cinzel text-xl text-amber-300 mb-4">Current Week: {currentInventory?.week_id}</h2>
          <div className="flex items-center gap-4 mb-4">
            <div className={`px-4 py-2 rounded ${currentInventory?.admin_curated ? 'bg-purple-950/30 border border-purple-700' : 'bg-zinc-800 border border-zinc-700'}`}>
              {currentInventory?.admin_curated ? (
                <span className="text-purple-300">🎯 Admin Curated</span>
              ) : (
                <span className="text-zinc-400">🎲 Random Generation</span>
              )}
            </div>
          </div>

          <div className="grid grid-cols-2 md:grid-cols-3 lg:grid-cols-6 gap-3">
            {currentInventory?.items?.map((item, idx) => (
              <div key={idx} data-testid={`merchant-current-slot-${idx + 1}`} className="bg-zinc-950 border border-zinc-800 rounded p-3">
                <div className="text-xs text-zinc-500 mb-1">Slot {idx + 1}</div>
                <div className="text-sm text-zinc-200 font-semibold">{item.name}</div>
                <div data-testid={`merchant-current-slot-price-${idx + 1}`} className="text-xs text-amber-400 mt-1">
                  {formatPriceSummary({ price_gold: item.price_gold, price_follis: item.price_follis })}
                </div>
              </div>
            ))}
          </div>

          {currentInventory?.admin_curated && (
            <button
              data-testid="merchant-clear-custom-inventory-button"
              onClick={clearCustomInventory}
              disabled={busy}
              className="mt-4 px-4 py-2 bg-red-950/30 border border-red-700 text-red-300 rounded hover:bg-red-900/30 transition-colors disabled:opacity-50 flex items-center gap-2"
            >
              <RefreshCw size={16} />
              Revert to Random Generation
            </button>
          )}
        </div>

        <div className="bg-zinc-900 border border-emerald-900/30 rounded-lg p-6 mb-6" data-testid="merchant-always-available-summary">
          <div className="flex flex-col lg:flex-row lg:items-start lg:justify-between gap-4 mb-5">
            <div>
              <h2 className="font-cinzel text-xl text-emerald-300">{alwaysAvailableConfig.title || "Forum Stewards' Shelf"}</h2>
              <p className="text-sm text-zinc-400 mt-1 max-w-3xl">
                {alwaysAvailableConfig.story || "While the merchant is away, trusted warehouse stewards keep a few staple goods available in the forum."}
              </p>
            </div>
            <div className="flex items-center gap-3">
              <div className="px-3 py-2 rounded border border-emerald-700/40 bg-emerald-950/20 text-sm text-emerald-200">
                {selectedAlwaysAvailableItems.length} item{selectedAlwaysAvailableItems.length === 1 ? "" : "s"} on shelf
              </div>
              <button
                data-testid="merchant-save-always-available-button"
                onClick={saveAlwaysAvailable}
                disabled={busy}
                className="px-4 py-2 text-sm bg-emerald-950/30 border border-emerald-700 text-emerald-200 rounded hover:bg-emerald-900/30 transition-colors disabled:opacity-50"
              >
                Save Forum Shelf
              </button>
            </div>
          </div>

          {selectedAlwaysAvailableItems.length > 0 ? (
            <div className="grid grid-cols-1 md:grid-cols-2 xl:grid-cols-4 gap-3">
              {selectedAlwaysAvailableItems.map((item) => {
                const settings = getAlwaysAvailableSettings(item.item_id);
                return (
                  <div
                    key={item.item_id}
                    data-testid={`merchant-always-available-item-${item.item_id}`}
                    className="bg-zinc-950 border border-emerald-900/30 rounded p-3"
                  >
                    <div className="text-sm font-semibold text-zinc-200">{item.name}</div>
                    <div className="text-xs text-amber-400 mt-1">{formatDraftPriceSummary(item)}</div>
                    <div className="text-xs text-emerald-300 mt-2">Weekly limit: {settings.purchase_limit}</div>
                  </div>
                );
              })}
            </div>
          ) : (
            <div className="text-sm text-zinc-500 border border-dashed border-zinc-700 rounded p-4">
              No forum-shelf items selected yet. Turn on <span className="text-emerald-300">Add to Forum Shelf</span> on any item below, then save.
            </div>
          )}
        </div>

        {/* Item Selection */}
        <div className="bg-zinc-900 border border-amber-900/30 rounded-lg p-6">
          <div className="flex items-center justify-between mb-6">
            <div>
              <h2 className="font-cinzel text-xl text-amber-300">
                Select {WEEKLY_ROTATION_ITEM_COUNT} Items ({selectedItems.length}/{WEEKLY_ROTATION_ITEM_COUNT})
              </h2>
              <p className="text-sm text-zinc-500 mt-1">Edit both prices here, choose the {WEEKLY_ROTATION_ITEM_COUNT} merchant items, and mark any staples for the forum shelf.</p>
            </div>
            <div className="flex gap-2">
              <button
                data-testid="merchant-clear-selection-button"
                onClick={() => setSelectedItems([])}
                disabled={selectedItems.length === 0}
                className="px-3 py-1 text-sm bg-zinc-800 border border-zinc-700 text-zinc-400 rounded hover:bg-zinc-700 transition-colors disabled:opacity-50"
              >
                Clear Selection
              </button>
              <button
                data-testid="merchant-save-prices-button"
                onClick={savePrices}
                disabled={busy || allItems.length === 0}
                className="px-4 py-2 text-sm bg-zinc-800 border border-zinc-700 text-zinc-300 rounded hover:bg-zinc-700 transition-colors disabled:opacity-50"
              >
                Save All Prices
              </button>
              <button
                data-testid="merchant-set-inventory-button"
                onClick={setMerchantInventory}
                disabled={selectedItems.length !== WEEKLY_ROTATION_ITEM_COUNT || busy}
                className={btn("primary") + " text-sm"}
              >
                Set Merchant Inventory
              </button>
            </div>
          </div>

          {/* Beast Boost Items */}
          <div className="mb-6">
            <h3 className="text-lg font-cinzel text-purple-300 mb-3">Beast Boost Items</h3>
            <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-3">
              {availableItems.beast_boost_items.map((item) => renderItemCard(item, "purple"))}
            </div>
          </div>

          {/* Non-Beast Items */}
          <div>
            <h3 className="text-lg font-cinzel text-blue-300 mb-3">Other Items</h3>
            <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-3">
              {availableItems.non_beast_items.map((item) => renderItemCard(item, "blue"))}
            </div>
          </div>
        </div>
      </div>
    </div>
  );
}
